import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { commits, branches, findCommit } from "../helpers/CommitLog";
import MyResume from "../static/resume/Mauricio_Gonzalez_s_Resume.pdf";

const PAGES = {
    "~": "/",
    home: "/",
    projects: "/projects",
    experience: "/experience",
    education: "/education",
    about: "/aboutme",
    aboutme: "/aboutme",
    chess: "/chess",
};

const HELP = [
    "available commands:",
    "  ls              list pages",
    "  cd <page>       go to a page (e.g. cd projects)",
    "  whoami          who is this guy",
    "  git log         my career as a commit graph",
    "  git status      where things stand right now",
    "  git branch      what else has been running",
    "  git show <hash> what changed in a commit",
    "  git checkout <hash|branch>  open it on the timeline",
    "  tcpdump         open the packet capture",
    "  resume          download my resume",
    "  contact         how to reach me",
    "  theme           toggle light/dark mode",
    "  clear           clear the screen",
    "  exit            close the terminal",
];

function Terminal({ open, onOpenChange, toggleTheme }) {
    const navigate = useNavigate();
    const [lines, setLines] = useState([
        "welcome to mauric10.com. type 'help' to get started",
    ]);
    const [input, setInput] = useState("");
    const inputRef = useRef(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [lines, open]);

    const run = (raw) => {
        const cmd = raw.trim();
        if (!cmd) return [];
        const [name, ...args] = cmd.split(/\s+/);

        switch (name) {
            case "help":
                return HELP;
            case "ls":
                return ["projects/  experience/  education/  about/  chess/"];
            case "cd": {
                const target = (args[0] || "~").replace(/^~\//, "").replace(/\/$/, "");
                if (target === "..") {
                    navigate("/");
                    onOpenChange(false);
                    return [];
                }
                const path = PAGES[target];
                if (path) {
                    navigate(path);
                    onOpenChange(false);
                    return [];
                }
                return [`cd: no such page: ${target}. try 'ls'`];
            }
            case "whoami":
                return [
                    "Mauricio Gonzalez",
                    "Backend Software Engineer @ bet365 · Go, distributed systems",
                    "B.S. Computer Science, Colorado State University",
                ];
            case "git": {
                const [sub, target] = args;
                if (sub === "log") {
                    return [
                        ...commits.map((commit) => {
                            const lane = "  ".repeat(commit.rail.lane);
                            const refs = commit.refs
                                ? ` (${commit.refs
                                      .map((ref) => ref.replace("HEAD -> ", "").split("/").pop())
                                      .join(", ")})`
                                : "";
                            return `${lane}* ${commit.hash} ${commit.period.padEnd(11)} ${commit.short}${refs}`;
                        }),
                        "",
                        "try: git show <hash>  ·  git checkout <hash>",
                    ];
                }
                if (sub === "status") {
                    const head = commits.find((commit) => commit.rail.head);
                    return [
                        "On branch main",
                        `HEAD at ${head.hash} (${head.short})`,
                        "",
                        "2 branches still open:",
                        "  mentoring/dps-student",
                        "  security/defcon",
                        "",
                        "nothing to commit, still working",
                    ];
                }
                if (sub === "branch") {
                    return branches.map((branch) => (branch === "main" ? `* ${branch}` : `  ${branch}`));
                }
                if (sub === "show" || sub === "checkout") {
                    if (!target) return [`usage: git ${sub} <hash|branch>`];
                    const commit = findCommit(target);
                    if (!commit) {
                        return [`error: pathspec '${target}' did not match any commit. try 'git log'`];
                    }
                    if (sub === "checkout") {
                        navigate(`/experience?commit=${commit.hash}`);
                        onOpenChange(false);
                        return [];
                    }
                    if (!commit.changes) return [`commit ${commit.hash}`, `    ${commit.title}`];
                    return [
                        `commit ${commit.hash}${commit.refs ? ` (${commit.refs.join(", ")})` : ""}`,
                        `Date:   ${commit.period}`,
                        "",
                        `    ${commit.title}`,
                        "",
                        ...commit.changes.map((change) => `+ ${change}`),
                    ];
                }
                return [`git: '${sub || ""}' is not a command here. try 'git log'`];
            }
            case "tcpdump":
            case "wireshark": {
                navigate("/");
                onOpenChange(false);
                // the skills section is lazy loaded, so wait for the card to exist
                let attempts = 0;
                const scroll = () => {
                    const capture = document.getElementById("capture");
                    if (capture) {
                        capture.scrollIntoView({ behavior: "smooth", block: "start" });
                    } else if (attempts < 20) {
                        attempts += 1;
                        window.setTimeout(scroll, 150);
                    }
                };
                window.setTimeout(scroll, 150);
                return [];
            }
            case "resume": {
                const link = document.createElement("a");
                link.href = MyResume;
                link.download = "Mauricio_Gonzalez_Resume.pdf";
                link.click();
                return ["downloading resume.pdf …"];
            }
            case "contact":
                return [
                    "email     mauricio@gonzalez-medina.com",
                    "linkedin  linkedin.com/in/mauric10",
                    "github    github.com/mauriciog88u1",
                ];
            case "theme":
                toggleTheme();
                return ["theme toggled"];
            case "clear":
                setLines([]);
                return null;
            case "exit":
                onOpenChange(false);
                return [];
            case "sudo":
                return ["nice try."];
            default:
                return [`command not found: ${name}. try 'help'`];
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const echoed = `$ ${input}`;
        const output = run(input);
        setInput("");
        if (output === null) return; // clear already reset lines
        setLines((prev) => [...prev, echoed, ...output]);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl gap-0 overflow-hidden border-zinc-800 bg-zinc-950 p-0 font-mono text-sm text-zinc-200"
                onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    inputRef.current?.focus();
                }}
            >
                <DialogTitle className="sr-only">Terminal</DialogTitle>
                <div className="flex items-center gap-1.5 border-b border-zinc-800 px-4 py-2.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
                    <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
                    <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
                    <span className="ml-2 text-xs text-zinc-500">mauricio@denver: ~</span>
                </div>
                <div
                    ref={scrollRef}
                    className="max-h-72 overflow-y-auto px-4 py-3"
                    onClick={() => inputRef.current?.focus()}
                >
                    {lines.map((line, index) => (
                        <p key={index} className="whitespace-pre-wrap leading-relaxed text-zinc-300">
                            {line}
                        </p>
                    ))}
                    <form onSubmit={handleSubmit} className="flex items-center gap-2">
                        <span className="shrink-0">
                            <span className="text-teal-400">mauricio@denver</span>
                            <span className="text-zinc-500">:~$</span>
                        </span>
                        <input
                            ref={inputRef}
                            value={input}
                            onChange={(event) => setInput(event.target.value)}
                            className="w-full bg-transparent text-zinc-100 caret-teal-400 outline-none"
                            spellCheck={false}
                            autoComplete="off"
                            aria-label="Terminal command input"
                        />
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default Terminal;
